import { InlineOption } from "@/Common/Components/Inputs/InlineOptionsControl";
import { NoYesNotApplicable } from "@/Patient/Models/ObstetricalFile/BritishColumbia/Enum/NoYesNotApplicable";
import { NoYesUndecided } from "@/Patient/Models/ObstetricalFile/BritishColumbia/Enum/NoYesUndecided";
import { PregnancyType } from "@/Patient/Models/ObstetricalFile/BritishColumbia/Part2/PregnancyType";
import { BaseBritishColumbiaObstetricalFileSubSection, BaseBritishColumbiaObstetricalFileSubSectionProps } from "../../../Subcomponent/BaseBritishColumbiaObstetricalFileSubSection";

class BritishColumbiaObstetricalFileSection16 extends BaseBritishColumbiaObstetricalFileSubSection<BaseBritishColumbiaObstetricalFileSubSectionProps, never> {
    private readonly section16Id = "Section16";

    public render(): JSX.Element {
        const model = this.props.form.Section16;

        return (
            <div className="section">
                {this.renderTitle(16, "ObstetricalFile_PerinatalConsiderationsAndReferralsLabel")}
                <table className="no-border-section-table">
                    <tbody>
                        <tr>
                            <td className="bold">{this.getString("ObstetricalFile_PregnancyTypeLabel")}</td>
                            <td>
                                {this.controlRenderer.renderInlineRadio({
                                    id: `${this.section16Id}_PregnancyType`,
                                    items: this.getPregancyTypeOptions(),
                                    value: model?.PregnancyType,
                                    onChange: value => this.props.mutateModelStateWithFormUpdate(s => s.Section16.PregnancyType = value),
                                    allowDeselect: true
                                })}
                            </td>
                        </tr>
                        <tr>
                            <td className="bold">{this.getString("ObstetricalFile_VaginalBirthAfterCesareanEligibleAt36WeeksAbbrevLabel")}</td>
                            <td>
                                {this.controlRenderer.renderInlineRadio({
                                    id: `${this.section16Id}_VaginalBirthAfterCesareanEligibleAt36Weeks`,
                                    items: this.getNoYesNotApplicableOptions(),
                                    value: model?.VaginalBirthAfterCesareanEligibleAt36Weeks,
                                    onChange: value => this.props.mutateModelStateWithFormUpdate(s => s.Section16.VaginalBirthAfterCesareanEligibleAt36Weeks = value),
                                    allowDeselect: true
                                })}
                            </td>
                        </tr>
                        <tr>
                            <td className="bold">{this.getString("ObstetricalFile_VaginalBirthAfterCesareanPlannedAt36WeeksAbbrevLabel")}</td>
                            <td>
                                {this.controlRenderer.renderInlineRadio({
                                    id: `${this.section16Id}_VaginalBirthAfterCesareanPlannedAt36Weeks`,
                                    items: this.getNoYesNotApplicableOptions(),
                                    value: model?.VaginalBirthAfterCesareanPlannedAt36Weeks,
                                    onChange: value => this.props.mutateModelStateWithFormUpdate(s => s.Section16.VaginalBirthAfterCesareanPlannedAt36Weeks = value),
                                    allowDeselect: true
                                })}
                            </td>
                        </tr>
                        <tr>
                            <td className="bold">{this.getString("ObstetricalFile_PlanToBreastfeedLabel")}</td>
                            <td>
                                {this.controlRenderer.renderInlineRadio({
                                    id: `${this.section16Id}_PlanToBreastfeed`,
                                    items: this.getNoYesUndecidedOptions(),
                                    value: model?.PlanToBreastfeed,
                                    onChange: value => this.props.mutateModelStateWithFormUpdate(s => s.Section16.PlanToBreastfeed = value),
                                    allowDeselect: true
                                })}
                            </td>
                        </tr>
                    </tbody>
                </table>
                {this.renderExpandableTextBox(
                    "ObstetricalFile_LifestyleSlashSubstanceUseLabel",
                    model?.LifestyleAndSubstanceUse,
                    value => this.props.mutateModelState(s => s.Section16.LifestyleAndSubstanceUse = value)
                )}
                {this.renderExpandableTextBox(
                    "PregnancyLabel",
                    model?.Pregnancy,
                    value => this.props.mutateModelState(s => s.Section16.Pregnancy = value),
                    "margin-sm-top"
                )}
                {this.renderExpandableTextBox(
                    "ObstetricalFile_LabourAndBirthLabel",
                    model?.LabourAndBirth,
                    value => this.props.mutateModelState(s => s.Section16.LabourAndBirth = value),
                    "margin-sm-top"
                )}
                {this.renderExpandableTextBox(
                    "BreastfeedingLabel",
                    model?.Breastfeeding,
                    value => this.props.mutateModelState(s => s.Section16.Breastfeeding = value),
                    "margin-sm-top"
                )}
                {this.renderExpandableTextBox(
                    "PostpartumLabel",
                    model?.Postpartum,
                    value => this.props.mutateModelState(s => s.Section16.Postpartum = value),
                    "margin-sm-top"
                )}
                {this.renderExpandableTextBox(
                    "ObstetricalFile_ContraceptionPlanLabel",
                    model?.ContraceptionPlan,
                    value => this.props.mutateModelState(s => s.Section16.ContraceptionPlan = value),
                    "margin-sm-top"
                )}
                {this.renderExpandableTextBox(
                    "NewbornLabel",
                    model?.Newborn,
                    value => this.props.mutateModelState(s => s.Section16.Newborn = value),
                    "margin-sm-top"
                )}
            </div>
        );
    }

    private getPregancyTypeOptions(): InlineOption<PregnancyType>[] {
        return [
            new InlineOption<PregnancyType>(PregnancyType.Singleton, this.getString("SingletonLabel")),
            new InlineOption<PregnancyType>(PregnancyType.Twin, this.getString("TwinLabel")),
            new InlineOption<PregnancyType>(PregnancyType.Multiple, this.getString("ObstetricalFile_Multiple3PlusLabel"))
        ];
    }

    private getNoYesNotApplicableOptions(): InlineOption<NoYesNotApplicable>[] {
        return [
            new InlineOption<NoYesNotApplicable>(NoYesNotApplicable.No, this.getString("NoLabel")),
            new InlineOption<NoYesNotApplicable>(NoYesNotApplicable.Yes, this.getString("YesLabel")),
            new InlineOption<NoYesNotApplicable>(NoYesNotApplicable.NotApplicable, this.getString("NotApplicableLabel"))
        ];
    }

    private getNoYesUndecidedOptions(): InlineOption<NoYesUndecided>[] {
        return [
            new InlineOption<NoYesUndecided>(NoYesUndecided.No, this.getString("NoLabel")),
            new InlineOption<NoYesUndecided>(NoYesUndecided.Yes, this.getString("YesLabel")),
            new InlineOption<NoYesUndecided>(NoYesUndecided.Undecided, this.getString("UndecidedLabel"))
        ];
    }

    private renderExpandableTextBox(label: string, value: string, onChange: (value: string) => void, className?: string): JSX.Element {
        return this.renderField({
            label: label,
            field: this.controlRenderer.renderResizableTextarea({
                value: value,
                onChange: value => onChange(value),
                className: `textarea-padding ${className ?? ""}`,
                minHeight: "34px"
            })
        });
    }
}

export { BritishColumbiaObstetricalFileSection16 };
